import React, { createContext, useContext, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, IconButton, Stack } from "@mui/material";
import {
  CircleCheckBig,
  XCircle,
  Info,
  AlertTriangle,
  Loader2,
  X,
} from "lucide-mui";
import { useTheme } from "./ThemeContext";

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback(
    (message, type = "success", containerId = null, action = null) => {
      const id = Date.now() + Math.random();
      const notification = { id, message, type, containerId, action };

      setNotifications(prev => [...prev, notification]);

      setTimeout(
        () => {
          setNotifications(prev => prev.filter(n => n.id !== id));
        },
        action ? 5000 : 3000,
      );
    },
    [],
  );

  const removeNotification = useCallback(id => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Group notifications by containerId
  const grouped = notifications.reduce((acc, n) => {
    const key = n.containerId || "__default__";
    acc[key] = acc[key] || [];
    acc[key].push(n);
    return acc;
  }, {});

  return (
    <NotificationContext.Provider
      value={{ addNotification, removeNotification }}
    >
      {children}

      {Object.entries(grouped).map(([key, notifs]) => {
        if (key === "__default__") {
          return (
            <Box
              key='__default__'
              sx={{
                position: "fixed",
                top: "20px",
                right: "20px",
                zIndex: 999999,
              }}
            >
              <Stack spacing={1}>
                {notifs.map(n => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    onRemove={removeNotification}
                  />
                ))}
              </Stack>
            </Box>
          );
        }

        const target = document.getElementById(key);
        if (!target) return null;

        return ReactDOM.createPortal(
          <Stack key={key} spacing={1}>
            {notifs.map(n => (
              <NotificationCard
                key={n.id}
                notification={n}
                onRemove={removeNotification}
              />
            ))}
          </Stack>,
          target,
        );
      })}
    </NotificationContext.Provider>
  );
};

const NotificationCard = ({ notification, onRemove }) => {
  const { isDark } = useTheme();
  const iconConfig = {
    success: { Icon: CircleCheckBig, color: "#52C41A" },
    error: { Icon: XCircle, color: "#FF4D4F" },
    info: { Icon: Info, color: "#1677FF" },
    normal: { Icon: Info, color: "#1677FF" },
    warning: { Icon: AlertTriangle, color: "#FAAD14" },
    loading: { Icon: Loader2, color: "#1677FF" },
  };

  const { Icon, color } = iconConfig[notification.type] || iconConfig.info;

  return (
    <Box
      sx={{
        borderRadius: "12px",
        p: "10px 14px",
        minWidth: 300,
        maxWidth: 400,
        background: isDark
          ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
          : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
        border: `1px solid ${isDark ? "#434950E5" : "#D5D9DF"}`,
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.4)"
          : "0 8px 24px rgba(0,0,0,0.1)",
        backdropFilter: "blur(8px)",
        animation: "slideIn 0.3s ease-out",
        "@keyframes slideIn": {
          from: { opacity: 0, transform: "translateY(12px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ color, display: "flex", flexShrink: 0 }}>
          <Icon
            sx={{
              height: "16px",
              width: "16px",
              ...(notification.type === "loading" && {
                animation: "spin 1s linear infinite",
              }),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "14px",
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              color: isDark ? "#ECEFF3" : "#434950",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            {notification.message}
          </Typography>
          {notification.action && (
            <Box
              onClick={async () => {
                try {
                  await notification.action.onClick();
                } finally {
                  onRemove(notification.id);
                }
              }}
              sx={{ cursor: "pointer", flexShrink: 0 }}
            >
              <Typography
                sx={{
                  color: isDark ? "#ECEFF3" : "#434950",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  whiteSpace: "nowrap",
                }}
              >
                {notification.action.label}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton
          size='small'
          onClick={() => onRemove(notification.id)}
          sx={{
            p: 0,
            color: isDark ? "#b3b3b3" : "#6B7280",
            "&:hover": { color: isDark ? "#e0e0e0" : "#434950" },
          }}
        >
          <X sx={{ height: "18px", width: "18px" }} />
        </IconButton>
      </Box>
    </Box>
  );
};
